import {Component, Input, OnInit} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {FormBuilder, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import { ibanValidator } from 'src/app/utils/validators/iban-validator';
import { GeneralService } from '../../services/general.service';
import { dniValidator, nieValidator, cifValidator, getMaxLengthByDoc } from 'src/app/utils/validators/spanish-id-validator';
import { UserBankingDataListener } from '../../interface/user-banking-data-listener';
import { UserBankingDataModel } from '../../interface/user-banking-data-model';
import { ApiAmenaService } from '../../services/api-amena.service';
import { brands } from 'src/app/shared/constantes';

@Component({
    selector: 'app-user-banking-data',
    templateUrl: './user-banking-data.component.html',
    styleUrls: ['./user-banking-data.component.css', '../../../../assets/css/amena-theme.css'],
    standalone: false
})
export class UserBankingDataComponent implements OnInit {
    @Input() model: UserBankingDataModel;
    @Input() countries: any[] = [];
    @Input() requestCompanyInfo = false;
    @Input() listener: UserBankingDataListener;
    @Input() userForm = null;

    public country = 'Spain';
    public requireCif = false;
    public formGroup: FormGroup;
    public maxLengthByDoc = 9;
    public documentTypes;
    public migrationData: any;
    public accountSelected: string = '0';

    constructor(
        private modalService: NgbModal, 
        private formBuilder: FormBuilder, 
        private generalService: GeneralService,
        private amenaApi: ApiAmenaService
    ) { }

    ngOnInit() {
        if (this.model === undefined) {
            this.model = new UserBankingDataModel();
        }

        this.formGroup = this.formBuilder.group({
            bank_account: [this.model.bank_account, [Validators.required, ibanValidator()]],
            account_doctype: [this.model.account_doctype, Validators.required],
            account_doc: [this.model.account_doc, Validators.required],
            account_firstName: [this.model.account_firstName, Validators.required],
            account_surname1: [this.model.account_surname1, Validators.required],
            account_surname2: [this.model.account_surname2, /*Validators.required*/],
            account_company: [this.model.account_company],
        });

        this.generalService.getEmptyFormUserDataBank()
            .subscribe((data) => {
                if (data) {
                    this.formGroup.get('bank_account').patchValue(data.bank_account);
                    this.formGroup.get('account_doctype').patchValue(data.account_doctype);
                    this.formGroup.get('account_doc').patchValue(data.account_doc);
                    this.formGroup.get('account_firstName').patchValue(data.account_firstName);
                    this.formGroup.get('account_surname1').patchValue(data.account_surname1);
                    this.formGroup.get('account_surname2').patchValue(data.account_surname2);
                }
            });

        this.amenaApi.getDocumentTypes()
            .subscribe((data: any) => {
                if (data) {
                    this.documentTypes = data;
                }
            });

        this.amenaApi.getMigrationData()
            .subscribe((value: any) => {
                this.migrationData = value;
            });
    }

    open(content) {
        this.setData(this.listener.getUserData());
        this.modalService.open(content, {size: 'lg', centered: true, backdrop: 'static', keyboard : false});
    }

    setData(data: UserBankingDataModel) {
        if (!this.formGroup.get('account_doctype').value) {
            this.formGroup.get('account_doctype').patchValue(data.account_doctype);
        }
        if (!this.formGroup.get('account_doc').value) {
            this.formGroup.get('account_doc').patchValue(data.account_doc);
        }
        if (!this.formGroup.get('account_firstName').value) {
            this.formGroup.get('account_firstName').patchValue(data.account_firstName);
        }
        if (!this.formGroup.get('account_surname1').value) {
            this.formGroup.get('account_surname1').patchValue(data.account_surname1);
        }
        if (!this.formGroup.get('account_surname2').value) {
            this.formGroup.get('account_surname2').patchValue(data.account_surname2);
        }
        if (!this.formGroup.get('account_company').value) {
            this.formGroup.get('account_company').patchValue(data.account_company);
        }
        this.fakeChangeDocumentEvent(this.formGroup, 'account_doctype');

        this.save();
    }

    checkFormGroup(): boolean {
        this.formGroup.markAllAsTouched();
        this.formGroup.updateValueAndValidity();

        return this.formGroup.valid;
    }

    save() {
        if (this.checkFormGroup()) {
            this.model.bank_account = this.formGroup.get('bank_account').value && this.formGroup.get('bank_account').value !== '' ? this.formGroup.get('bank_account').value.toString().replace(/ +?/g, '') : this.formGroup.get('bank_account').value;
            this.model.account_doctype = this.formGroup.get('account_doctype').value;
            this.model.account_doc = this.formGroup.get('account_doc').value;
            this.model.account_firstName = this.formGroup.get('account_firstName').value;
            this.model.account_surname1 = this.formGroup.get('account_surname1').value;
            this.model.account_surname2 = this.formGroup.get('account_surname2').value;
            this.model.account_company = this.formGroup.get('account_company').value;

            this.listener.onSuccessfulValidation(this.model);
            this.modalService.dismissAll();
        } else {
            const elements: any = document.getElementsByClassName('mat-input-element ng-invalid');
            if (elements.length > 0) {
                elements[0].focus();
            }
        }
    }

    changeTypeDocument(event): void {
        const docValidators: ValidatorFn[] = [Validators.required];

        this.requireCif = event.target.value === 'CIF';

        if (this.requireCif) {
            this.formGroup.get('account_company').setValidators(Validators.required);
        } else {
            this.formGroup.get('account_company').clearValidators();
        }

        switch (event.target.value) {
            case 'DNI':
                docValidators.push(dniValidator());
                break;
            case 'NIF':
                docValidators.push(dniValidator());
                break;
            case 'NIE':
                docValidators.push(nieValidator());
                break;
            case 'CIF':
                docValidators.push(cifValidator());
                break;
            default:
        }

        this.formGroup.get('account_doc').setValidators(docValidators);
        this.formGroup.get('account_doc').updateValueAndValidity();
        this.formGroup.get('account_company').updateValueAndValidity();
    }

    onIbanChange(event) {
        this.formGroup.get('bank_account').patchValue(event.target.value);
        if (this.formGroup.get('bank_account').status === 'VALID') {
            this.setData(this.listener.getUserData());
        }
        this.save();
    }

    copyUserData() {
        const userData = this.userForm;
        if (userData !== null) {
            this.formGroup.get('account_doctype').patchValue(userData.get('doctype').value);
            this.formGroup.get('account_doc').patchValue(userData.get('doc').value);
            this.formGroup.get('account_firstName').patchValue(userData.get('firstName').value);
            this.formGroup.get('account_surname1').patchValue(userData.get('surname1').value);
            this.formGroup.get('account_surname2').patchValue(userData.get('surname2').value);
            this.formGroup.get('account_company').patchValue(userData.get('company').value);
            this.fakeChangeDocumentEvent(userData, 'doctype');
        }
    }

    setMaxLengthByDoc(type) {
        this.maxLengthByDoc = getMaxLengthByDoc(brands.amena, type);
    }

    selectTypeAccount(type) {
        if (type === '0') {
            this.accountSelected = '0';           
            this.formGroup.get('bank_account').setValidators([Validators.required, ibanValidator()]);
            this.formGroup.get('account_doctype').setValidators(Validators.required);
            this.formGroup.get('account_doc').setValidators(Validators.required);
            this.formGroup.get('account_firstName').setValidators(Validators.required);
            this.formGroup.get('account_surname1').setValidators(Validators.required);
            this.fakeChangeDocumentEvent(this.formGroup, 'account_doctype');
        } else if (type === '1') {
            const userData = this.listener.getUserData();
            this.accountSelected = '1';
            this.model.bank_account = null;
            this.formGroup.get('bank_account').patchValue(this.model.bank_account);
            this.formGroup.get('bank_account').clearValidators();
            this.formGroup.get('account_doctype').patchValue(userData.account_doctype);
            this.formGroup.get('account_doc').patchValue(userData.account_doc);
            this.formGroup.get('account_doctype').clearValidators();
            this.formGroup.get('account_doc').clearValidators();
            this.formGroup.get('account_firstName').clearValidators();
            this.formGroup.get('account_surname1').clearValidators();
        }
        this.formGroup.get('bank_account').updateValueAndValidity();
        this.formGroup.get('account_doctype').updateValueAndValidity();
        this.formGroup.get('account_doc').updateValueAndValidity();
        this.formGroup.get('account_firstName').updateValueAndValidity();
        this.formGroup.get('account_surname1').updateValueAndValidity();
    }

    fakeChangeDocumentEvent(form, nameForm) {
        const fakeChangeEvent: any = {
            target: {
                value: form.get(nameForm).value
            }
        };
        this.changeTypeDocument(fakeChangeEvent);
    }
}
